import { useQuery } from '@tanstack/react-query'

import { GQL_CLIENT } from 'src/graphqlClient'
import { AccountType } from 'src/utils/enums'
import { useIdentity } from '@/hooks/auth/useIdentity'
import type { Account } from 'types'

/**
 * Whether the signed-in commercial owns anything at all, per hub area.
 *
 * A row probe rather than a count: the question is "is there at least one",
 * and `limit: 1` answers it without aggregating the whole book. Hasura's
 * row-level permissions scope both selections to the caller, so a salesman
 * sees their own and a company its salesmen's — including the #653 rule that
 * withholds architect-ordered studies from the commercial side, which is
 * exactly the scoping the nav should reflect.
 */
const PRESENCE_QUERY = `
  query HubNavPresence {
    projects(limit: 1) {
      id
    }
    urban_analysis(limit: 1) {
      id
    }
  }
`

type PresenceResponse = {
  projects: Array<{ id: number }>
  urban_analysis: Array<{ id: number }>
}

export type HubNavPresence = {
  hasProjects: boolean
  hasUrbanAnalyses: boolean
  isLoading: boolean
}

/**
 * Only the two hub roles are asked; every other role gets `true` and no query,
 * because this exists solely to decide whether a COMMERCIAL's menu should
 * carry an entry they can neither use nor fill.
 *
 * **Optimistic while loading**, unlike `useCreationRights`, which fails closed.
 * The two are answering different questions. A create button that appears and
 * then has to be retracted would have offered a wizard the server refuses — so
 * absence is the safe default there. A nav entry, by contrast, leads to a
 * legitimate (if empty) page; hiding the primary navigation on every page load
 * and popping it back in is the worse outcome. So the entry stays until we
 * positively know it is both empty and unusable.
 */
export const useHubNavPresence = (): HubNavPresence => {
  const { data: identity } = useIdentity<Account>()
  const accountType = identity?.account_type
  const isCommercial =
    accountType === AccountType.SALESMAN || accountType === AccountType.SOCIETY

  const { data, isLoading } = useQuery<PresenceResponse>({
    queryKey: ['hub-nav-presence', identity?.id],
    enabled: isCommercial && !!identity,
    queryFn: () =>
      GQL_CLIENT.request(PRESENCE_QUERY) as Promise<PresenceResponse>,
  })

  if (!isCommercial) {
    return { hasProjects: true, hasUrbanAnalyses: true, isLoading: false }
  }

  if (isLoading || !data) {
    return { hasProjects: true, hasUrbanAnalyses: true, isLoading: true }
  }

  return {
    hasProjects: data.projects.length > 0,
    hasUrbanAnalyses: data.urban_analysis.length > 0,
    isLoading: false,
  }
}
